import React, { useState, useEffect, useMemo } from 'react'
import { AsyncPaginate } from 'react-select-async-paginate'
import { Typography } from '@mui/material'
import { PMPSectionInputLabel } from 'src/mentor/components/PrescriptionMakingPage/PrescriptionMakingPageStyles'
import { commonStylesPrescriptionSelect } from 'src/mentor/components/PrescriptionMakingPage/PrescriptionMakingPageComponents'
import { fetchOptions, lookupOptionsByCodes } from 'src/mentor/services/emrOptionsApi'

/**
 * AsyncSelectField - Renders QUERY type fields with searchable select
 * @param {object} field - EmrFieldDef with optionSource.type === "QUERY"
 * @param {string|string[]} value - Current value (code or array of codes)
 * @param {function} onChange - (code, display) => void for SELECT_ONE
 * @param {function} onChangeMulti - (codes, items) => void for SELECT_MULTI
 * @param {boolean} isMulti - Whether this is a multi-select field
 */
export default function AsyncSelectField({ field, value, onChange, onChangeMulti, isMulti }) {
  // const [selectedOptions, setSelectedOptions] = useState([])
  const selectedOptions = useMemo(() => {
    if (isMulti) {
      return value?.map((val) => ({ value: val?.code, label: val?.display })) || []
    } else {
      return value ? [{ value: value?.code, label: value?.display }] : []
    }
  }, [isMulti, value])

  const optionSource = field?.optionSource

  // loadOptions function for AsyncPaginate (handles debouncing automatically)
  const loadOptions = async (inputValue, _prevOptions, additional) => {
    const options = await fetchOptions(
      optionSource?.queryName,
      inputValue,
      optionSource?.params || {},
      50, // pageSize
    )
    return {
      options,
      hasMore: false,
      additional: {
        page: additional.page,
      },
    }
  }

  // Handle change for SELECT_ONE
  const handleChangeSingle = (selected) => {
    if (onChange) {
      onChange(selected?.value || null, selected?.label || null, selected?.label || null)
    }
  }

  // Handle change for SELECT_MULTI
  const handleChangeMulti = (selected) => {
    const selectedArray = selected || []
    const codes = selectedArray.map((opt) => opt.value)
    const items = selectedArray.map((opt) => ({
      code: opt.value,
      display: opt.label,
    }))

    if (onChangeMulti) {
      onChangeMulti(codes, items)
    }
  }

  return (
    <div
      key={field.code}
      style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}
    >
      <PMPSectionInputLabel>{field.label}</PMPSectionInputLabel>
      <div style={{ width: '100%' }}>
        <AsyncPaginate
          isMulti={isMulti}
          value={selectedOptions}
          onChange={isMulti ? handleChangeMulti : handleChangeSingle}
          loadOptions={loadOptions}
          debounceTimeout={300}
          menuPortalTarget={document.body}
          additional={{
            page: 1,
          }}
          placeholder={field.placeholder || 'Search...'}
          isDisabled={field.readOnly}
          isClearable
          styles={commonStylesPrescriptionSelect}
          noOptionsMessage={({ inputValue }) => `No results for "${inputValue}"`}
        />
        {field.helpText && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {field.helpText}
          </Typography>
        )}
      </div>
    </div>
  )
}
